import React, {FormEvent} from "react";
import {Formik} from "formik";
import {RadioGroupField, styled, Text} from "@reside/ui";

import {WorkflowOption} from "./types";
import {Spinner} from "../spinner";
import {useFacilityWorkflowsQuery} from "./hooks/useFacilityWorkflowsQuery";

type Props = Readonly<{
  /** When set, loads workflows for that facility. */
  facilityId?: string;
  selectedWorkflow: WorkflowOption;
  onSelect: (workflow: WorkflowOption) => void;
}>;

type FormValues = Readonly<{
  startWorkflow: string;
}>;

export const WorkflowList = ({
  facilityId,
  selectedWorkflow,
  onSelect,
}: Props) => {
  const {data: facilityWorkflows, isLoading: isLoadingWorkflows} =
    useFacilityWorkflowsQuery(facilityId);

  const workflows = facilityWorkflows?.workflows ?? [];

  if (isLoadingWorkflows) {
    return (
      <FieldWrapper>
        <SpinnerWrapper>
          <Spinner relative />
        </SpinnerWrapper>
      </FieldWrapper>
    );
  }

  if (workflows.length === 0) {
    return (
      <FieldWrapper>
        <LoadingMessage>
          No workflows available for this facility.
        </LoadingMessage>
      </FieldWrapper>
    );
  }

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={{startWorkflow: selectedWorkflow.type}}
      onSubmit={() => {}}
    >
      {() => (
        <FieldWrapper>
          <RadioGroupWrapper>
            <RadioGroupField
              name="startWorkflow"
              options={workflows.map(workflow => ({
                value: workflow.type,
                label: workflow.label,
              }))}
              onChange={(event: FormEvent<HTMLInputElement>) => {
                const workflow = workflows.find(
                  item => item.type === event.currentTarget.value,
                );

                if (workflow) {
                  onSelect(workflow);
                }
              }}
              renderOption={(element, option) => (
                <WorkflowCard
                  key={String(option.value)}
                  $selected={option.value === selectedWorkflow.type}
                  data-test-id={`startWorkflowOption-${option.value}`}
                >
                  <RadioOption>{element}</RadioOption>
                </WorkflowCard>
              )}
            />
          </RadioGroupWrapper>
        </FieldWrapper>
      )}
    </Formik>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[4]}px;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const SpinnerWrapper = styled.div`
  margin: auto;
  display: block;
  top: 10px;
  height: 0;
`;

const LoadingMessage = styled(Text)`
  font-size: 14px;
  color: ${({theme}) => theme.color.gray100};
`;

const RadioGroupWrapper = styled.div`
  width: 100%;

  > div {
    width: 100%;
  }

  > div > div:last-child {
    width: 100%;
  }

  > div > div:last-child > div {
    width: 100%;
    margin-right: 0;
    gap: ${({theme}) => theme.space[3]}px;
  }
`;

const WorkflowCard = styled.div<{$selected: boolean}>`
  border: 1px solid
    ${({theme, $selected}) =>
      $selected ? theme.color.primary : theme.color.borderGray};
  border-radius: 8px;
  padding: ${({theme}) => theme.space[4]}px ${({theme}) => theme.space[5]}px;
  background: ${({theme, $selected}) =>
    $selected ? theme.color.lightBlue10 : theme.color.white};
`;

const RadioOption = styled.div`
  width: 100%;

  label {
    display: flex;
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    padding: 0;
    border: none;
  }

  p {
    flex: 1;
    min-width: 0;
    padding-left: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.25;
    pointer-events: none;
  }
`;
